import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand, BrandLogo, brandFooterText } from "../context/BrandContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, ArrowRight } from "lucide-react";

const BG_IMG =
  "https://images.unsplash.com/photo-1663192053762-c55fe58e0d13?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [siteUrl, setSiteUrl] = useState("interactmarketing.com");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password, siteUrl);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Login failed. Please check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAFAF9]">
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-16 fade-in">
        <div className="mb-16">
          <BrandLogo className="h-14 w-auto" textClass="text-3xl" />
          <p className="mt-3 font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
            {brand.tagline || "Content Review Portal"}
          </p>
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
          Review your articles.
        </h1>
        <p className="text-neutral-600 text-base mb-12 max-w-md leading-relaxed">
          Sign in with the WordPress credentials provided by your {brand.display_name} team to
          proofread, edit, and approve articles before they go live.
        </p>

        <form onSubmit={submit} className="max-w-md space-y-6">
          <div className="space-y-2">
            <Label htmlFor="site-url" className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
              Website Domain
            </Label>
            <Input
              id="site-url"
              data-testid="login-domain-input"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              placeholder="yoursite.com"
              className="h-12 rounded-lg bg-white"
              autoComplete="url"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="username" className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
              WordPress Username
            </Label>
            <Input
              id="username"
              data-testid="login-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your-username"
              className="h-12 rounded-lg bg-white"
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
              Application Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="xxxx xxxx xxxx xxxx"
              className="h-12 rounded-lg bg-white"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p data-testid="login-error-message" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading || !username || !password || !siteUrl}
            className="w-full h-12 rounded-full bg-[var(--brand-primary)] hover:opacity-90 active:scale-95 transition-colors text-white font-semibold"
          >
            {loading ? "Signing in..." : "Sign In"}
            {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </form>

        <div className="mt-12 flex items-center gap-2 text-xs text-neutral-400">
          <Lock className="h-3.5 w-3.5" />
          <span>Secure WordPress connection &middot; Demo access: demo / demo</span>
        </div>
        {brandFooterText(brand) && (
          <p className="mt-6 text-xs text-neutral-400" data-testid="brand-footer">
            {brandFooterText(brand)}
          </p>
        )}
      </div>

      <div className="hidden lg:block relative">
        <img src={BG_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/60" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="font-article text-white/90 text-2xl italic leading-relaxed">
            "One place for your clients to review, refine, and sign off — without ever touching
            WordPress."
          </p>
          {brand.slug === "interactmarketing" ? (
            <img src="/interact-logo-secondary.png" alt="Interact Marketing" className="h-12 w-auto mt-6 opacity-90" />
          ) : (
            <p className="font-heading font-black text-2xl text-white mt-6">{brand.display_name}</p>
          )}
        </div>
      </div>
    </div>
  );
}
